import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaCheckCircle,
  FaPaperPlane,
} from "react-icons/fa";
import "./Pages.css";

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create FormData object for submission
    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("phone", formData.phone);
    formDataToSend.append("company", formData.company);
    formDataToSend.append("message", formData.message);

    // Submit to Formspree
    try {
      const response = await fetch("https://formspree.io/f/mojdpkvw", {
        method: "POST",
        body: formDataToSend,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setIsSubmitted(true);
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        alert("There was an error submitting the form. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main>
      <Helmet>
        <title>Contact Us | Seagull Partners</title>
        <meta
          name="description"
          content="Contact Seagull Partners to discuss B2B data, lead generation, and growth solutions. Share your goals and get a tailored plan from our experts within 24 hours."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">Let's Talk About Your Growth</h1>
          <p className="page-hero-subtitle">
            Have questions or ready to explore custom solutions? Our team at
            seagull is here to help your business grow smarter, faster, and
            more efficiently.
          </p>
          <a href="#contact-form" className="btn">
            📩 Schedule a Consultation
          </a>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="section contact-section">
        <div className="container">
          <div className="contact-content">
            {/* Contact Form */}
            <div className="contact-form-container">
              <h2 className="section-title" id="contact-form">
                Get in Touch
              </h2>

              {isSubmitted ? (
                <div className="success-message">
                  <FaCheckCircle className="success-icon" />
                  <h3>Thank You!</h3>
                  <p>
                    Your message has been sent successfully to{" "}
                    <strong>info.seagullpartners@gmail.com</strong>. We'll get back to
                    you soon.
                  </p>
                  <button
                    className="btn"
                    onClick={() => setIsSubmitted(false)}
                    style={{ marginTop: "20px" }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  {/* Hidden fields for Formspree */}
                  <input
                    type="hidden"
                    name="_subject"
                    value="New Contact Form Submission - Seagull"
                  />
                  <input type="hidden" name="_replyto" value={formData.email} />
                  <input
                    type="text"
                    name="_gotcha"
                    style={{ display: "none" }}
                  />

                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your Name"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number (Optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company Name (Optional)</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message / Inquiry *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your project or questions..."
                      rows="6"
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="btn submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane style={{ marginRight: "8px" }} />
                        Send Message
                      </>
                    )}
                  </button>

                  <p className="privacy-note">
                    We respect your privacy. Your information will never be
                    shared with third parties.
                  </p>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div className="contact-info-container">
              <h2 className="section-title">Reach Us Directly</h2>
              <p className="contact-info-intro">
                You can also contact us directly using the information below:
              </p>

              <div className="contact-details">
                <div className="contact-detail">
                  <div className="contact-icon-wrapper">
                    <FaEnvelope className="contact-detail-icon" />
                  </div>
                  <div className="contact-detail-content">
                    <h3>Email</h3>
                    <a
                      href="mailto:seagullpartner@gmail.com"
                      className="contact-link"
                    >
                      info.seagullpartners@gmail.com
                    </a>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-icon-wrapper">
                    <FaPhone className="contact-detail-icon" />
                  </div>
                  <div className="contact-detail-content">
                    <h3>Phone</h3>
                    <a href="tel:+919876543210" className="contact-link">
                      +(1) 3235540745
                    </a>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-icon-wrapper">
                    <FaMapMarkerAlt className="contact-detail-icon" />
                  </div>
                  <div className="contact-detail-content">
                    <h3>Address</h3>
                    <address className="contact-address">
                      {/* 4680 Preston Rd #150, 
                      <br /> */}
                      Frisco, TX 75034
                    </address>
                  </div>
                </div>
              </div>

              {/* <div className="office-hours">
                <h3>Office Hours</h3>
                <p>Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                <p>Saturday: 10:00 AM - 2:00 PM IST</p>
                <p>Sunday: Closed</p>
              </div> */}

              <div className="response-time">
                <h3>Response Time</h3>
                <p>
                  We typically respond within 24 hours during business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative Simple Form (HTML-only backup) */}
      <section
        className="section simple-form-section"
        style={{ display: "none" }}
      >
        <div className="container">
          <div className="simple-form-container">
            <h2 className="section-title">Simple Contact Form</h2>
            <p className="section-subtitle">
              Use this form if the main form doesn't work:
            </p>

            <form
              action="https://formspree.io/f/mojdpkvw"
              method="POST"
              className="simple-form"
            >
              <input
                type="hidden"
                name="_subject"
                value="New Contact Form - Seagull"
              />

              <div className="form-group">
                <label>Your Name:</label>
                <input type="text" name="name" required />
              </div>

              <div className="form-group">
                <label>Your Email:</label>
                <input type="email" name="email" required />
              </div>

              <div className="form-group">
                <label>Your Message:</label>
                <textarea name="message" required></textarea>
              </div>

              <button type="submit" className="btn">
                Send via seagullpartners
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Quick Consultation CTA */}
      <section className="section quick-consultation">
        <div className="container">
          <div className="content-section text-center">
            <h2 className="section-title">Need Immediate Assistance?</h2>
            <p className="section-intro">
              Book a free 30-minute consultation with our growth experts.
            </p>
            <div className="consultation-options">
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=info.seagullpartners@gmail.com&su=Free%20Consultation%20Request&body=Hello%20Seagull%20Team,%0D%0AI%20would%20like%20to%20book%20a%20free%20consultation."
                target="_blank"
                rel="noopener noreferrer"
                className="btn consultation-btn"
              >
                📧 Email Us Now
              </a>


              {/* <a
              href="https://wa.me/9876543210?text=Hello%20Seagull%20Team,%20I%20would%20like%20a%20free%20consultation."
              target="_blank"
              rel="noopener noreferrer"
              className="btn consultation-btn"
            >
              💬 WhatsApp Us
            </a> */}

            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
